import { Combobox } from "@base-ui-components/react/combobox";
import { ChevronDown, Check, Plus, X } from "lucide-react";
import * as React from "react";

export interface MasterComboboxItem {
  id: string;
  label: string;
  sublabel?: string | null;
}

export interface MasterComboboxProps {
  value: string | null;
  onValueChange: (id: string | null) => void;
  items: MasterComboboxItem[];
  onSearch?: (query: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  /** 検索デバウンス (ms)。onSearch 呼出をこの間隔まで間引く。 */
  searchDebounceMs?: number;
  /** 一覧に無い選択中アイテムのラベル解決用 (編集モード初期表示など)。 */
  selectedItem?: MasterComboboxItem | null;
  /** 指定するとポップアップ下部に「＋ 新規登録」フッターを表示する。引数は現在の検索文字列。 */
  onCreateNew?: (query: string) => void;
  createNewLabel?: string;
}

const triggerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  flexShrink: 0,
  border: "none",
  borderLeft: "0.5px solid var(--rule-soft)",
  background: "transparent",
  cursor: "pointer",
  color: "var(--ink-3)",
  height: "100%",
  padding: 0,
  outline: "none",
};

const clearStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  flexShrink: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--ink-4)",
  height: "100%",
  padding: 0,
  outline: "none",
};

export function MasterCombobox({
  value,
  onValueChange,
  items,
  onSearch,
  isLoading,
  placeholder = "検索して選択…",
  emptyLabel = "項目",
  searchDebounceMs = 250,
  selectedItem,
  onCreateNew,
  createNewLabel = "新規登録",
}: MasterComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const queryRef = React.useRef("");
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // 選択時点のアイテムを保持し、検索で items から外れてもラベルを解決できるようにする。
  const lastSelectedRef = React.useRef<MasterComboboxItem | null>(null);

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const resolveItem = (id: string): MasterComboboxItem | null =>
    items.find((i) => i.id === id) ??
    (lastSelectedRef.current?.id === id ? lastSelectedRef.current : null) ??
    (selectedItem?.id === id ? selectedItem : null);

  const handleInputValueChange = (query: string) => {
    queryRef.current = query;
    if (!onSearch) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(query), searchDebounceMs);
  };

  const handleValueChange = (id: string | null) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastSelectedRef.current = id ? resolveItem(id) : null;
    onValueChange(id);
  };

  return (
    <Combobox.Root
      value={value ?? null}
      onValueChange={(v) => handleValueChange(v ?? null)}
      open={open}
      onOpenChange={setOpen}
      onInputValueChange={handleInputValueChange}
      itemToStringLabel={(id) => (id ? (resolveItem(id)?.label ?? "") : "")}
      autoHighlight
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: "0.5px solid var(--rule-soft)",
          borderRadius: "var(--radius)",
          background: "var(--bg-raised)",
          height: 30,
          overflow: "hidden",
          transition: "box-shadow 0.1s",
          boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        }}
        onFocus={(e) => {
          (e.currentTarget as HTMLElement).style.boxShadow =
            "0 0 0 1px var(--accent), 0 1px 2px 0 rgb(0 0 0 / 0.05)";
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            (e.currentTarget as HTMLElement).style.boxShadow = "0 1px 2px 0 rgb(0 0 0 / 0.05)";
          }
        }}
      >
        <Combobox.Input
          placeholder={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            background: "transparent",
            fontSize: 12,
            padding: "0 10px",
            outline: "none",
            height: "100%",
            color: "var(--ink)",
          }}
        />
        <Combobox.Clear style={clearStyle} onClick={() => handleValueChange(null)}>
          <X size={11} />
        </Combobox.Clear>
        <Combobox.Trigger style={triggerStyle}>
          <ChevronDown size={12} />
        </Combobox.Trigger>
      </div>
      <Combobox.Portal>
        <Combobox.Positioner sideOffset={3} style={{ zIndex: 200 }}>
          <Combobox.Popup
            style={{
              background: "var(--bg-raised)",
              border: "0.5px solid var(--rule)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "0 8px 24px oklch(0 0 0 / 0.10), 0 2px 6px oklch(0 0 0 / 0.06)",
              maxHeight: 260,
              overflowY: "auto",
              width: "var(--anchor-width)",
              padding: 4,
            }}
          >
            {/* サーバー検索のため base-ui の Combobox.Empty は使わない (Root に items を渡さない構成では
                常に「0件」扱いになり、候補があっても空メッセージが出てしまう)。items の有無で自前判定する。 */}
            {items.length === 0 && (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: 11,
                  color: "var(--ink-3)",
                  textAlign: "center",
                }}
              >
                {isLoading ? "検索中…" : `${emptyLabel}が見つかりません`}
              </div>
            )}
            {/* 再検索中は前回結果を残したまま薄くし、リストが空に点滅するのを防ぐ */}
            <Combobox.List style={isLoading ? { opacity: 0.55 } : undefined}>
              {items.map((item) => (
                <MasterComboboxItemRow key={item.id} item={item} />
              ))}
            </Combobox.List>
            {onCreateNew && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setOpen(false);
                  onCreateNew(queryRef.current);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  width: "100%",
                  padding: "7px 10px",
                  marginTop: 2,
                  border: "none",
                  borderTop: "0.5px solid var(--rule-soft)",
                  borderRadius: 2,
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 11,
                  color: "var(--accent)",
                  outline: "none",
                }}
              >
                <Plus size={11} style={{ flexShrink: 0 }} />
                {createNewLabel}
              </button>
            )}
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}

function MasterComboboxItemRow({ item }: { item: MasterComboboxItem }) {
  return (
    <Combobox.Item
      value={item.id}
      render={(props, state) => (
        <div
          {...props}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 10px",
            borderRadius: 2,
            cursor: "pointer",
            background: state.highlighted
              ? "var(--accent-wash)"
              : state.selected
                ? "oklch(0.97 0.01 150)"
                : "transparent",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                color: state.highlighted ? "var(--accent)" : "var(--ink)",
                fontWeight: state.selected ? 600 : 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </div>
            {item.sublabel && (
              <div
                style={{
                  fontSize: 10,
                  color: state.highlighted ? "oklch(0.55 0.12 35)" : "var(--ink-3)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  marginTop: 1,
                }}
              >
                {item.sublabel}
              </div>
            )}
          </div>
          {state.selected && <Check size={12} style={{ flexShrink: 0, color: "var(--ok)" }} />}
        </div>
      )}
    />
  );
}
